import { useEffect, useMemo, useState } from 'react'
import swal from 'sweetalert'
import { getAuth, onAuthStateChanged } from 'firebase/auth'
import { arrayUnion, collection, doc, getDocs, query, updateDoc, where } from 'firebase/firestore'
import { db } from '../firebase'
import { buildSecondaryCourseEmail } from '../utils/emailTemplates'

export const normalizeCourseName = (value) => (value || '').toString().trim().toLowerCase()

export const formatDuration = (durationValue) => {
  if (durationValue === null || durationValue === undefined || durationValue === '') {
    return 'Not set'
  }
  if (typeof durationValue === 'number') {
    return `${durationValue} months`
  }
  const raw = String(durationValue).trim()
  if (!raw) return 'Not set'
  if (/\d/.test(raw) && /(month|week|year|day)/i.test(raw)) return raw
  const asNumber = Number(raw)
  if (!Number.isNaN(asNumber)) return `${asNumber} months`
  return raw
}

export const formatDateValue = (value) => {
  if (!value) return 'Not set'
  if (value?.toDate) return value.toDate().toLocaleDateString()
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return String(value)
  return parsed.toLocaleDateString()
}

export const formatIntakeLabel = (intake) => {
  if (!intake) return 'Not set'
  if (typeof intake === 'string') return intake
  if (intake.intakeDate && intake.campus) return `${intake.intakeDate} • ${intake.campus}`
  return intake.intakeDate || intake.rawIntakeDate || intake.campus || 'Not set'
}

const normalizeCampus = (value) => (value || '').toString().trim().toLowerCase()

const mapStudentStatus = (studentStatus) => {
  if (studentStatus === 'Ongoing') return 'Inactive'
  if (studentStatus === 'Active') return 'Ongoing'
  if (studentStatus === 'Suspended') return 'Suspended'
  if (studentStatus === 'Completed') return 'Completed'
  return 'Pending'
}

const formatPhoneNumber = (number) => {
  if (!number) return null
  let cleaned = String(number).replace(/[^0-9]/g, '')
  if (cleaned.startsWith('0')) {
    cleaned = `254${cleaned.slice(1)}`
  } else if (cleaned.startsWith('7') || cleaned.startsWith('1')) {
    cleaned = `254${cleaned}`
  }
  if (cleaned.length === 12 && cleaned.startsWith('254')) {
    return cleaned
  }
  return cleaned || null
}

function useAcademicCourses() {
  const auth = getAuth()
  const [courseName, setCourseName] = useState('')
  const [courseDesc, setCourseDesc] = useState('')
  const [courseDuration, setCourseDuration] = useState('')
  const [admissionDate, setAdmissionDate] = useState('')
  const [studentStatus, setStudentStatus] = useState('')
  const [currentUserStatus, setCurrentUserStatus] = useState('')
  const [studentName, setStudentName] = useState('')
  const [userEmail, setUserEmail] = useState('')
  const [userPhone, setUserPhone] = useState('')
  const [token, setToken] = useState('')
  const [userDocId, setUserDocId] = useState('')
  const [allCourses, setAllCourses] = useState([])
  const [allIntakes, setAllIntakes] = useState([])
  const [secondaryCourses, setSecondaryCourses] = useState([])
  const [selectedSecondaryCourse, setSelectedSecondaryCourse] = useState('')
  const [selectedIntakeId, setSelectedIntakeId] = useState('')
  const [selectedCampus, setSelectedCampus] = useState('')
  const [isAddingCourse, setIsAddingCourse] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  const canAddSecondaryCourse = currentUserStatus.trim().toLowerCase() === 'student'

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setIsLoading(false)
        return
      }

      setIsLoading(true)
      try {
        const userSnapshot = await getDocs(query(collection(db, 'users'), where('userId', '==', user.uid)))
        let nextMainCourse = ''
        let nextStudentStatus = ''

        userSnapshot.forEach((docSnap) => {
          const data = docSnap.data()
          nextMainCourse = data.course || ''
          nextStudentStatus = data.Status || ''
          const userStatus = data.userStatus || data.Status || ''
          const extraCourses = data.secondaryCourses || []
          const admissionTimestamp = data.admissionDate

          setStudentName(data.bio || data.userName || 'Student')
          setUserEmail(data.userEmail || '')
          setUserPhone(data.phoneNumber || '')
          setCurrentUserStatus(userStatus)
          setStudentStatus(mapStudentStatus(nextStudentStatus))
          setUserDocId(docSnap.id)

          if (admissionTimestamp?.toDate) {
            setAdmissionDate(admissionTimestamp.toDate().toISOString())
          } else if (admissionTimestamp) {
            setAdmissionDate(String(admissionTimestamp))
          } else {
            setAdmissionDate('')
          }

          const normalizedSecondary = Array.isArray(extraCourses)
            ? extraCourses.map((entry) => {
                if (typeof entry === 'string') {
                  return { name: entry, intake: '', status: 'Pending' }
                }
                return {
                  name: entry?.name || entry?.courseName || '',
                  intake: entry?.intake || entry?.intakeDate || '',
                  intakeId: entry?.intakeId || '',
                  rawIntakeDate: entry?.rawIntakeDate || '',
                  campus: entry?.campus || '',
                  status: entry?.status || 'Pending',
                  admissionDate: entry?.admissionDate || ''
                }
              }).filter((entry) => entry.name)
            : []

          setSecondaryCourses(normalizedSecondary)
        })

        const coursesSnapshot = await getDocs(collection(db, 'courses'))
        const courseList = []
        coursesSnapshot.forEach((docSnap) => {
          const data = docSnap.data()
          const name = data.mcourseName || data.courseName || data.name
          if (!name) return
          courseList.push({
            id: docSnap.id,
            name,
            description: data.mcourseDescription || data.courseDescription || data.description || '',
            duration: data.mcourseDuration || data.courseDuration || ''
          })
        })
        setAllCourses(courseList)

        const matchedMainCourse = courseList.find((course) => normalizeCourseName(course.name) === normalizeCourseName(nextMainCourse))
        setCourseName(nextMainCourse || matchedMainCourse?.name || '')
        setCourseDesc(matchedMainCourse?.description || '')
        setCourseDuration(matchedMainCourse?.duration || '')

        const intakeSnapshot = await getDocs(collection(db, 'Intakes'))
        const intakeList = []
        intakeSnapshot.forEach((docSnap) => {
          const data = docSnap.data()
          intakeList.push({
            id: data.docID || docSnap.id,
            courseName: data.CourseName || data.courseName || '',
            intakeDate: data.intakeDate || '',
            rawIntakeDate: data.rawIntakeDate || '',
            campus: data.Campus || data.campus || ''
          })
        })
        setAllIntakes(intakeList)
      } catch (error) {
        console.error('Error loading academic data: ', error)
      } finally {
        setIsLoading(false)
      }
    })

    return () => unsubscribe()
  }, [auth])

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        user.getIdToken().then(setToken).catch((error) => console.error(error))
      }
    })

    return () => unsubscribe()
  }, [auth])

  const availableSecondaryCourses = useMemo(() => {
    const selectedNames = secondaryCourses.map((course) => course.name)
    return allCourses.filter((course) => {
      if (!course?.name) return false
      if (course.name === courseName) return false
      return !selectedNames.includes(course.name)
    })
  }, [allCourses, courseName, secondaryCourses])

  const availableIntakes = useMemo(() => {
    if (!selectedSecondaryCourse) return []
    const selectedKey = normalizeCourseName(selectedSecondaryCourse)
    return allIntakes
      .filter((intake) => normalizeCourseName(intake.courseName) === selectedKey)
      .sort((first, second) => (first.rawIntakeDate || '').localeCompare(second.rawIntakeDate || ''))
  }, [allIntakes, selectedSecondaryCourse])

  const selectedIntake = useMemo(() => {
    if (!selectedIntakeId) return null
    return allIntakes.find((intake) => intake.id === selectedIntakeId) || null
  }, [allIntakes, selectedIntakeId])

  const availableCampusOptions = useMemo(() => {
    if (!selectedIntake) return []
    const campus = selectedIntake.campus || ''
    if (!campus) return []
    if (normalizeCampus(campus) === 'general') {
      return ['CBD Campus', 'Virtual Classes']
    }
    return [campus]
  }, [selectedIntake])

  const campusRequiresChoice = useMemo(
    () => availableCampusOptions.length > 1,
    [availableCampusOptions]
  )

  useEffect(() => {
    setSelectedIntakeId('')
  }, [selectedSecondaryCourse])

  useEffect(() => {
    if (!selectedIntakeId) {
      setSelectedCampus('')
      return
    }
    if (availableCampusOptions.length === 1) {
      setSelectedCampus(availableCampusOptions[0])
      return
    }
    if (!availableCampusOptions.includes(selectedCampus)) {
      setSelectedCampus('')
    }
  }, [availableCampusOptions, selectedCampus, selectedIntakeId])

  const secondaryCourseCards = useMemo(() => (
    secondaryCourses.map((entry) => {
      const entryName = entry?.name
      const match = allCourses.find((course) => course.name === entryName)
      const intakeMatch = allIntakes.find((intake) => intake.id === entry?.intakeId)
        || allIntakes.find((intake) => (
          intake.rawIntakeDate === entry?.rawIntakeDate
          && normalizeCourseName(intake.courseName) === normalizeCourseName(entryName)
        ))

      return {
        name: entryName || 'Course',
        description: match?.description || '',
        duration: match?.duration || '',
        intake: intakeMatch || {
          intakeDate: entry?.intake || entry?.intakeDate || '',
          campus: entry?.campus || '',
          rawIntakeDate: entry?.rawIntakeDate || ''
        },
        status: entry?.status || 'Pending',
        admissionDate: entry?.admissionDate || '',
        type: 'secondary'
      }
    })
  ), [allCourses, allIntakes, secondaryCourses])

  const primaryCourse = useMemo(() => ({
    name: courseName || 'Your Course',
    description: courseDesc || 'Course description will appear here once it is published.',
    duration: courseDuration || '',
    status: studentStatus || 'Pending',
    admissionDate,
    type: 'primary'
  }), [admissionDate, courseDesc, courseDuration, courseName, studentStatus])

  const courseRecords = useMemo(() => {
    const records = []
    if (courseName) {
      records.push(primaryCourse)
    }
    return [...records, ...secondaryCourseCards]
  }, [courseName, primaryCourse, secondaryCourseCards])

  const sendCourseAddedNotifications = async (courseEntry) => {
    const formattedPhone = formatPhoneNumber(userPhone)
    const intakeLabel = formatIntakeLabel({
      intakeDate: courseEntry?.intakeDate || '',
      rawIntakeDate: courseEntry?.rawIntakeDate || '',
      campus: courseEntry?.campus || ''
    })
    const emailSubject = `Secondary course added: ${courseEntry?.name || 'Course'}`
    const emailMessage = buildSecondaryCourseEmail({
      studentName,
      courseName: courseEntry?.name || 'Course',
      intakeLabel
    })

    if (userEmail) {
      const headers = { 'Content-Type': 'application/json' }
      if (token) headers.Authorization = `Bearer ${token}`
      fetch('https://us-central1-ubunifu-college.cloudfunctions.net/api/send-email', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          email: userEmail,
          message: emailMessage,
          subject: emailSubject
        })
      })
    }

    if (formattedPhone) {
      const headers = { 'Content-Type': 'application/json' }
      if (token) headers.Authorization = `Bearer ${token}`
      fetch('https://us-central1-ubunifu-college.cloudfunctions.net/api/send-sms', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          phoneNumber: formattedPhone,
          message: `Hi ${studentName || 'Student'}, your secondary course "${courseEntry?.name || 'course'}" has been added for intake ${intakeLabel}. Please pay your invoice to complete admission. - Ubunifu College`
        })
      })
    }
  }

  const addSecondaryCourse = async () => {
    if (!canAddSecondaryCourse) {
      swal('Unavailable', 'Only students can add a new course from this page.', 'info')
      return null
    }
    if (!selectedSecondaryCourse) {
      swal('Select a course', 'Choose a course before adding it.', 'warning')
      return null
    }
    if (!selectedIntakeId) {
      swal('Select an intake', 'Choose the intake you want to join.', 'warning')
      return null
    }
    if (campusRequiresChoice && !selectedCampus) {
      swal('Select a campus', 'Choose the campus you want to attend for this intake.', 'warning')
      return null
    }
    if (!userDocId) {
      swal('Hold on', 'We could not find your profile record. Please reload and try again.', 'error')
      return null
    }

    try {
      setIsAddingCourse(true)
      if (secondaryCourses.some((entry) => entry.name === selectedSecondaryCourse)) {
        swal('Already added', 'You have already added this course.', 'info')
        return null
      }

      const intakeInfo = allIntakes.find((intake) => intake.id === selectedIntakeId)
      const newEntry = {
        name: selectedSecondaryCourse,
        intakeId: selectedIntakeId,
        intakeDate: intakeInfo?.intakeDate || '',
        rawIntakeDate: intakeInfo?.rawIntakeDate || '',
        campus: selectedCampus || intakeInfo?.campus || '',
        status: 'Pending',
        admissionDate: '',
        addedAt: Date.now()
      }

      setSecondaryCourses((current) => (
        current.some((entry) => entry.name === selectedSecondaryCourse) ? current : [...current, newEntry]
      ))
      setSelectedSecondaryCourse('')
      setSelectedIntakeId('')
      setSelectedCampus('')

      const updatePromise = updateDoc(doc(db, 'users', userDocId), {
        secondaryCourses: arrayUnion(newEntry),
        pendingSecondaryCourse: newEntry
      })

      updatePromise.then(() => sendCourseAddedNotifications(newEntry)).catch((error) => {
        console.error('Error sending notifications: ', error)
      })

      const result = await Promise.race([
        updatePromise.then(() => 'ok'),
        new Promise((resolve) => setTimeout(() => resolve('timeout'), 6000))
      ])

      if (result === 'timeout') {
        swal('Saving', 'Course added locally. Syncing with the server...', 'info')
      } else {
        swal('Added', 'The course has been saved as a secondary course.', 'success')
      }

      return newEntry
    } catch (error) {
      console.error('Error adding secondary course: ', error)
      swal('Error', 'Could not add the course. Please try again.', 'error')
      return null
    } finally {
      setIsAddingCourse(false)
    }
  }

  return {
    isLoading,
    isAddingCourse,
    canAddSecondaryCourse,
    primaryCourse,
    courseRecords,
    availableSecondaryCourses,
    availableIntakes,
    availableCampusOptions,
    campusRequiresChoice,
    selectedSecondaryCourse,
    setSelectedSecondaryCourse,
    selectedIntakeId,
    setSelectedIntakeId,
    selectedCampus,
    setSelectedCampus,
    addSecondaryCourse
  }
}

export default useAcademicCourses
